import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  faChevronLeft,
  faPlus,
  faPencil,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row } from "Components/Core";

import { getVideoDetail } from "Api/Pages/VideoListApi";
import { deleteCoachPromotionalOffer } from "Api/Pages/CoachPromotionalOffersApi";
import { CoachPromotionalOffer } from "Interfaces/Components/Shared/AssetVideoDetailInterface";
import CoachPromotionalOfferModal from "Components/Shared/CoachPromotionalOfferModal";
import { setToastMessage } from "App/commonReducer";
import { useAppDispatch, useAppSelector } from "App/hooks";
import { isAllowedTo } from "Utils/helper";
import { SUCCESS } from "App/constants";

interface AssetVideoDetailProps {
  videoId: number;
  onBackClick: () => void;
}

const AssetVideoDetail = (props: AssetVideoDetailProps) => {
  const { videoId, onBackClick } = props;
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const {
    userDetails: { permissions },
  } = useAppSelector((state) => state.loginReducer);

  const canManageOffers = isAllowedTo(
    "CoachPromotionalOffer",
    "create",
    permissions
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingOffer, setEditingOffer] =
    useState<CoachPromotionalOffer | null>(null);

  const { data } = useQuery({
    queryKey: ["asset-details", videoId],
    queryFn: () => getVideoDetail(videoId),
  });

  if (!data) {
    return null;
  }

  const {
    description,
    title,
    master_sport_name,
    kpoint_gcc_id,
    graph_attribute,
    asset_evaluation_category,
    asset_evaluation_sub_category,
    asset_evaluation_relations,
    uploaded_by,
    coach_promotional_offers = [],
  } = data;

  const tags = asset_evaluation_relations
    .map((obj) => obj.evaluable_title)
    .join(" > ");

  const canAddMore = canManageOffers && coach_promotional_offers.length < 1;

  const handleOpenAdd = () => {
    setEditingOffer(null);
    setIsModalVisible(true);
  };

  const handleOpenEdit = (offer: CoachPromotionalOffer) => {
    setEditingOffer(offer);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingOffer(null);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries(["asset-details", videoId]);
  };

  const handleDelete = async (offerId: number) => {
    try {
      await deleteCoachPromotionalOffer(videoId, offerId);
      dispatch(setToastMessage([["Promotional offer deleted"], SUCCESS]));
      queryClient.invalidateQueries(["asset-details", videoId]);
    } catch {
      // errors handled by axios interceptor
    }
  };

  return (
    <div className="ss-page-content d-flex flex-column flex-1 scroll-y bg-white">
      <header className="ss-page-header d-flex align-items-center justify-content-start gap-2 px-4 ">
        <FontAwesomeIcon
          icon={faChevronLeft}
          type="button"
          onClick={onBackClick}
        />
        <h2 className="fs-md mb-0 fw-medium">Video Details</h2>
      </header>
      <hr className="ss-page-seprator mx-4 my-0" />
      <div className="d-flex flex-column flex-1 scroll-y p-4 gap-4">
        <div>
          <label className="fs-xs">Title</label>
          <p className="fs-sm fw-bold mb-0">{title}</p>
        </div>
        <div>
          <label className="fs-xs">Description</label>
          <p className="fs-sm fw-bold mb-0">{description || "---"}</p>
        </div>
        <div>
          <label className="fs-xs">GCC ID</label>
          <p className="fs-sm fw-bold mb-0">{kpoint_gcc_id || "---"}</p>
        </div>
        <div>
          <label className="fs-xs">Uploaded By</label>
          <p className="fs-sm fw-bold mb-0">{uploaded_by || "---"}</p>
        </div>
        <Row>
          {master_sport_name && (
            <Col lg={4}>
              <label className="fs-xs">Sport</label>
              <p className="fs-sm fw-bold mb-0">{master_sport_name}</p>
            </Col>
          )}
          {graph_attribute && (
            <Col lg={4}>
              <label className="fs-xs">Parameter</label>
              <p className="fs-sm fw-bold mb-0">{graph_attribute}</p>
            </Col>
          )}
        </Row>
        <Row>
          {asset_evaluation_category?.title && (
            <Col lg={4}>
              <label className="fs-xs">Specific Parameter</label>
              <p className="fs-sm fw-bold mb-0">
                {asset_evaluation_category.title}
              </p>
            </Col>
          )}
          {asset_evaluation_sub_category?.title && (
            <Col lg={4}>
              <label className="fs-xs">Specific Skill</label>
              <p className="fs-sm fw-bold mb-0">
                {asset_evaluation_sub_category.title}
              </p>
            </Col>
          )}
        </Row>
        {tags.length !== 0 && (
          <div>
            <label className="fs-xs">Multiple Tags</label>
            <div className="d-flex gap-2"># {tags}</div>
          </div>
        )}

        {/* Coach Promotional Offers */}
        {canManageOffers && (
          <div>
            <hr className="my-2" />
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fs-sm fw-semibold mb-0">
                Coach Promotional Offers
              </h5>
              {canAddMore && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleOpenAdd}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Offer
                </Button>
              )}
            </div>

            {coach_promotional_offers.length === 0 ? (
              <p className="fs-xs text-muted mb-0">
                No promotional offers added yet.
              </p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {coach_promotional_offers.map((offer) => (
                  <div
                    key={offer.id}
                    className="sports-details-wrap p-3 position-relative"
                  >
                    <div className="d-flex align-items-start gap-3">
                      <div className="flex-grow-1">
                        <Row className="g-2">
                          <Col lg={3}>
                            <label className="fs-xs">Coach ID</label>
                            <p className="fs-sm fw-bold mb-0">
                              {offer.coach_id}
                            </p>
                          </Col>
                          <Col lg={3}>
                            <label className="fs-xs">Bundle</label>
                            <p className="fs-sm fw-bold mb-0">
                              {offer.video_count} Videos
                            </p>
                          </Col>
                          <Col lg={3}>
                            <label className="fs-xs">Bundle Rate (INR)</label>
                            <p className="fs-sm fw-bold mb-0">
                              ₹{offer.review_rates.INR}
                            </p>
                          </Col>
                          <Col lg={3}>
                            <label className="fs-xs">Bundle Rate (USD)</label>
                            <p className="fs-sm fw-bold mb-0">
                              ${offer.review_rates.USD}
                            </p>
                          </Col>
                        </Row>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Edit offer"
                          onClick={() => handleOpenEdit(offer)}
                        >
                          <FontAwesomeIcon icon={faPencil} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Delete offer"
                          onClick={() => handleDelete(offer.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <CoachPromotionalOfferModal
        isVisible={isModalVisible}
        assetId={videoId}
        existingOffer={editingOffer}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default AssetVideoDetail;
